import { getResultCoverConfig, getTitleParts } from '../data/result-cover-config';
import type { Hero, MatchResult } from '../types';

type Props = {
  hero: Hero;
  result: MatchResult;
  onSave: () => void;
  onReport: () => void;
};

function FloralFrame({ kind }: { kind: 'keyword' | 'quote' | 'button' }) {
  if (kind === 'quote') {
    return (
      <svg className="xl-frame" viewBox="0 0 720 208" preserveAspectRatio="none" aria-hidden="true">
        <path className="xl-frame-fill" d="M49 7H671c22 0 40 18 40 40v114c0 22-18 40-40 40H49c-22 0-40-18-40-40V47C9 25 27 7 49 7Z" />
        <path d="M51 14H669c19 0 34 15 34 34v112c0 19-15 34-34 34H51c-19 0-34-15-34-34V48c0-19 15-34 34-34Z" />
        <path className="xl-frame-inner" d="M61 25H659c18 0 32 14 32 32v94c0 18-14 32-32 32H61c-18 0-32-14-32-32V57c0-18 14-32 32-32Z" />
        <g className="xl-blossom xl-blossom-left" transform="translate(23 23)">
          <ellipse cx="0" cy="-14" rx="7" ry="15" /><ellipse cx="14" cy="0" rx="15" ry="7" /><ellipse cx="0" cy="14" rx="7" ry="15" /><ellipse cx="-14" cy="0" rx="15" ry="7" /><circle r="4" />
        </g>
        <g className="xl-blossom xl-blossom-right" transform="translate(690 179)">
          <ellipse cx="0" cy="-14" rx="7" ry="15" /><ellipse cx="14" cy="0" rx="15" ry="7" /><ellipse cx="0" cy="14" rx="7" ry="15" /><ellipse cx="-14" cy="0" rx="15" ry="7" /><circle r="4" />
        </g>
        <path className="xl-branch" d="M10 157c23-8 38-22 51-45M710 52c-23 8-38 22-51 45" />
      </svg>
    );
  }

  const viewBox = kind === 'button' ? '0 0 330 92' : '0 0 300 72';
  const shape = kind === 'button'
    ? 'M42 4H288c14 0 25 9 30 22l8 20-8 20c-5 13-16 22-30 22H42c-14 0-25-9-30-22L4 46l8-20C17 13 28 4 42 4Z'
    : 'M38 4H262c13 0 24 8 29 20l5 12-5 12c-5 12-16 20-29 20H38C25 68 14 60 9 48L4 36l5-12C14 12 25 4 38 4Z';

  return (
    <svg className="xl-frame" viewBox={viewBox} preserveAspectRatio="none" aria-hidden="true">
      <path className="xl-frame-fill" d={shape} />
      <path d={shape} />
      <path className="xl-frame-inner" d={shape} transform="translate(5 4) scale(.968 .91)" />
      <g className="xl-side-ornament" transform={kind === 'button' ? 'translate(12 46)' : 'translate(11 36)'}>
        <ellipse cx="0" cy="-8" rx="4" ry="8" /><ellipse cx="8" cy="0" rx="8" ry="4" /><ellipse cx="0" cy="8" rx="4" ry="8" /><ellipse cx="-8" cy="0" rx="8" ry="4" /><circle r="3" />
      </g>
      <g className="xl-side-ornament" transform={kind === 'button' ? 'translate(318 46)' : 'translate(289 36)'}>
        <ellipse cx="0" cy="-8" rx="4" ry="8" /><ellipse cx="8" cy="0" rx="8" ry="4" /><ellipse cx="0" cy="8" rx="4" ry="8" /><ellipse cx="-8" cy="0" rx="8" ry="4" /><circle r="3" />
      </g>
    </svg>
  );
}

export function HeroResultCover({ hero, result, onSave, onReport }: Props) {
  const config = getResultCoverConfig(hero);
  const titleParts = config.titleLayout === 'vertical' ? [...hero.name] : getTitleParts(hero);
  const scoreText = String(result.displayMatch);
  const className = [
    'result-cover',
    'cinematic-cover',
    config.themeClass,
    config.lightArtwork ? 'art-light' : 'art-dark',
    `title-${config.titleContrast}-contrast`,
    `score-${config.scoreContrast}-contrast`,
    `title-${config.titleLayout}`,
    `hero-${hero.id}`,
    hero.id === 20 ? 'xiangliu-cover' : '',
  ].filter(Boolean).join(' ');

  return (
    <section className={className} id="result-cover">
      <img className="result-background" src={hero.result_bg} alt="" width="941" height="1672" fetchPriority="high" decoding="sync" />
      <div className="result-shade" />
      <section className="xl-layout" aria-labelledby="result-name">
        <p className="xl-eyebrow"><span>✿</span>你的盖世英雄<span>✿</span></p>
        {config.titleAsset ? (
          <>
            <h1 id="result-name" className="sr-only">{hero.name}</h1>
            <div
              className={`xl-title-slot title-slot-length-${hero.name.length} title-slot-${config.titleLayout}`}
              aria-hidden="true"
            >
              <img
                className="xl-title-art"
                src={config.titleAsset}
                alt=""
                fetchPriority="high"
                decoding="sync"
              />
            </div>
          </>
        ) : (
          <h1 id="result-name" aria-label={hero.name} className={`hero-title-text hero-title-${config.titleLayout} title-length-${hero.name.length}`}>
            {titleParts.map((part, index) => <span key={`${part}-${index}`}>{part}</span>)}
          </h1>
        )}

        <div className="xl-match" aria-label={`命定契合度 ${result.displayMatch}%`}>
          <span>命定契合度</span>
          <strong><span className="match-score-leading">{scoreText.slice(0, -1)}</span><span className="match-score-ones">{scoreText.slice(-1)}</span><small>%</small></strong>
          <i aria-hidden="true" />
        </div>

        <div className="xl-keywords" aria-label="关系关键词">
          {hero.keywords.map((keyword) => (
            <span key={keyword}><FloralFrame kind="keyword" /><b>{keyword}</b></span>
          ))}
        </div>

        <blockquote className="xl-quote">
          <FloralFrame kind="quote" />
          <span className="xl-quote-open" aria-hidden="true">“</span>
          <p>{config.quoteLines.map((line) => <span key={line}>{line}</span>)}</p>
          <span className="xl-quote-close" aria-hidden="true">”</span>
        </blockquote>

        <div className="xl-actions">
          <button type="button" className="xl-save" onClick={onSave}>
            <FloralFrame kind="button" /><span>保存结果</span>
          </button>
          <button type="button" className="xl-report-button" onClick={onReport}>
            <FloralFrame kind="button" /><span>查看完整报告</span>
          </button>
        </div>
      </section>
    </section>
  );
}
